// src/services/AccountService.mjs
//
// Account lifecycle: email change, data export, deletion and restoration.
import apiClient from "../config/axiosConfig.js";

/**
 * Reconcile a completed email change.
 *
 * The change itself happens client-side via Firebase's `verifyBeforeUpdateEmail`,
 * which only updates the Firebase Auth record. QDex holds the address in two more
 * places (`users/{uid}.email` and every `personalAccessTokens.userEmail`), so this
 * call is what stops them drifting. The backend reads the new address from the
 * verified token, not from us.
 */
export async function syncEmail() {
    const response = await apiClient.post("/api/auth/sync-email");
    return response.data;
}

/**
 * What deleting this account would actually do — which workspaces get destroyed,
 * which are simply left, and which block the deletion until ownership is
 * transferred. Drives the confirmation screen so it can state specifics instead
 * of a generic warning.
 */
export async function getDeletionPreview() {
    const response = await apiClient.get("/api/auth/deletion-preview");
    return response.data;
}

/**
 * Schedule deletion. Destroys nothing — it starts a 30-day grace period during
 * which the account is locked but fully restorable. `confirmEmail` must match the
 * signed-in address exactly.
 */
export async function requestAccountDeletion(confirmEmail) {
    const response = await apiClient.post(
        "/api/auth/delete-account",
        { confirmEmail },
        { skipGlobalErrorToast: true },
    );
    return response.data;
}

/** Cancel a scheduled deletion. Reachable while the account is locked. */
export async function cancelAccountDeletion() {
    const response = await apiClient.post("/api/auth/cancel-deletion", null, {
        skipGlobalErrorToast: true,
    });
    return response.data;
}

/**
 * Download everything QDex holds that is personal to this user, as JSON.
 *
 * Fetched as a blob and saved client-side rather than navigating to the URL,
 * because the endpoint needs the Authorization header that only apiClient adds.
 */
export async function downloadAccountExport() {
    const response = await apiClient.get("/api/auth/export", { responseType: "blob" });

    const url = URL.createObjectURL(new Blob([response.data], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "qdex-account-export.json";
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoking immediately can race the download in some browsers; a tick is enough.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The caller's own security log. */
export async function getMySecurityEvents(limit = 50) {
    const response = await apiClient.get("/api/auth/events", { params: { limit } });
    return response.data.events;
}
